<template>
  <div v-if="$apollo.loading || loading">
    <spinner />
  </div>
  <main v-else class="content">
    <section class="main-section">
      <article v-if="error" class="main-section__content">
        <div class="accept-invite-title error-title">
          {{ adminAllMessage(error) }}
        </div>
        <div>
          <main-button
            class="accept-button"
            depressed
            :text="adminText('adminAll.go_to_organizations_7df133', 'Go to organizations')"
            @click="goTo(routes.organizer.organizations)"
          />
        </div>
      </article>
      <article v-else class="main-section__content">
        <div
          :style="{
            backgroundImage: 'url(' + image + ')',
            backgroundColor: image ? 'transparent' : '#ffffff25',
          }"
          :class="type === 'ORGANIZATION' ? 'logo-image-container' : 'event-image-container'"
        />
        <div class="accept-invite-title">
          {{ adminAllMessage(title) }}
        </div>
        <div>
          <main-button
            class="accept-button"
            depressed
            :text="type === 'ORGANIZATION' ? adminText('adminAll.otvori_organizaciju_ba4936', 'otvori organizaciju') : adminText('adminAll.otvori_event_0764c6', 'otvori event')"
            @click="accept()"
          />
        </div>
      </article>
    </section>
  </main>
</template>

<script>
import AdminAllMessages from '@/components/admin/AdminAllMessages'
import AdminContext from '@/admin-i18n/context'
import { GET_LOGGED_USER } from '../../graphql/user.graphql'
import { ACCEPT_INVITE } from '../../graphql/invite.graphql'
import { OrganizationType } from '@/types'

export default {
  mixins: [AdminContext, AdminAllMessages],
  props: {
    token: {
      type: String,
    },
  },
  data() {
    return {
      loading: true,
      image: null,
      title: null,
      type: null,
      organizationSlug: null,
      organizationType: null,
      eventId: null,
      user: null,
      error: null,
    }
  },
  methods: {
    getOrganizationRoute() {
      if (this.organizationType === OrganizationType.THEATER) {
        return this.routes.main.theaterOrganizationSidebar
      }

      if (this.organizationType === OrganizationType.SPORT_ORGANIZATION) {
        return this.routes.main.sportOrganizationSidebar
      }

      return this.routes.main.organizationSidebar
    },
    getEventRoute() {
      if (this.organizationType === OrganizationType.THEATER) {
        return this.routes.main.theaterOrganizerSidebar
      }

      if (this.organizationType === OrganizationType.SPORT_ORGANIZATION) {
        return this.routes.main.sportOrganizerSidebar
      }

      return this.routes.main.organizerSidebar
    },
    accept() {
      if (this.type === 'ORGANIZATION') {
        this.goTo(this.getOrganizationRoute(), {
          organizationSlug: this.organizationSlug,
        })
      } else {
        this.goTo(this.getEventRoute(), {
          organizationSlug: this.organizationSlug,
          eventId: this.eventId,
        })
      }
    },
    getErrorMessage(err) {
      const msg = (err && err.message) || ''
      if (msg.includes('already accepted')) return this.adminAllNotice('This invite has already been accepted.')
      if (msg.includes('expired'))
        return this.adminAllNotice('This invite has expired. Please ask the organizer to resend it.')
      if (msg.includes('doesn\'t exist')) return this.adminAllNotice('This invite is no longer valid.')
      if (msg.includes('Wrong account signed in to accept this invitation'))
        return this.adminAllNotice('You\'re signed in with the wrong account to accept this invitation.')

      return this.adminAllNotice('Something went wrong. Please try again later.')
    },
  },
  apollo: {
    me: {
      query: GET_LOGGED_USER,
      async result({ data, loading }) {
        if (!loading && data) {
          if (!data.me) {
            const redirect = window.location.href
            window.location.href = `${
              process.env.VUE_APP_FRONTEND_URL
            }/login?redirect_url=${encodeURIComponent(redirect)}`
          } else {
            this.user = data.me
            try {
              const { errors, data: newData } = await this.$apollo.mutate({
                mutation: ACCEPT_INVITE,
                variables: { token: this.token },
              })

              if (errors) {
                this.error = this.getErrorMessage(errors[0])
              } else {
                this.image = newData.acceptInvite.image
                this.title =
                  newData.acceptInvite.type === 'ORGANIZATION'
                    ? this.adminAllNotice('Uspješno ste postali dio {{value1}} organizacije', { value1: newData.acceptInvite.name })
                    : this.adminAllNotice('Uspješno ste postali dio eventa: {{value1}}', { value1: newData.acceptInvite.name })
                this.type = newData.acceptInvite.type
                this.organizationSlug = newData.acceptInvite.organizationSlug
                this.organizationType = newData.acceptInvite.organizationType
                this.eventId = newData.acceptInvite.eventId
              }
            } catch (e) {
              const gqlErr = e && e.graphQLErrors && e.graphQLErrors[0]
              this.error = this.getErrorMessage(gqlErr || e)
            }
            this.loading = false
          }
        }
      },
    },
  },
}
</script>

<style scoped>
.logo-image-container {
  height: 11.375rem;
  width: 11.375rem;
  background-size: cover;
  border-radius: 6rem !important;
}
.event-image-container {
  height: 11.375rem;
  width: 20rem;
  background-size: cover;
  object-fit: cover;
  border-radius: 4px;
}
.main-section__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.accept-invite-title {
  font-family: Raleway;
  font-style: normal;
  font-weight: 800;
  font-size: 72px;
  line-height: 120%;
  text-align: center;
  letter-spacing: -0.03em;
  color: #333333;
  margin-top: 3.5rem;
  max-width: 42rem;
}
.accept-button {
  margin-top: 2rem;
}
.content {
  display: grid;
  grid-auto-rows: auto;
  gap: 13.125rem;
  margin-bottom: 13.125rem;
}
.main-section {
  max-width: 1216px;
  padding: 0 2.625rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2.625rem;
  align-items: center;
}
.main-section__content {
  margin: 2.625rem 0;
}

@media screen and (max-width: 800px) {
  .accept-invite-title {
    font-size: 48px;
  }
}

@media screen and (max-width: 480px) {
  .accept-invite-title {
    font-size: 32px;
  }
  .event-image-container {
    width: 16rem;
  }
}
.error-logo {
  height: 40px;
  margin-bottom: 2rem;
}
.error-title {
  font-size: 36px;
  color: #666;
}
@media screen and (max-width: 800px) {
  .error-title {
    font-size: 28px;
  }
}
</style>
